import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppStore, AppDispatch, AppState } from '../../../store';
import { Box, Collapse, Alert, AlertTitle } from '@mui/material';
import { ErrorMessage } from '../../ErrorMessage';
import { EActionType, EAction, LanguageFormFields, EDataTableType } from '../../../data/Constants';
import { resetDialogMessage } from '../../../store/states/DialogSlice';
import {
  getLanguages,
  createLanguage,
  getLanguageDetails,
  updateLanguage,
} from '../../../store/states/LanguageSlice';
import { resetAlertMessage, setAlertMessage } from '../../../store/states/AlertMessageSlice';
import { resetError } from '../../../store/states/ErrorSlice';
import { GenericSimpleForm } from '../../Layouts/GenericSimpleForm';
import { TFunction } from 'i18next';

import type { IFormData, IError, IManipulationProps, IAPIResponse } from '../../../../types';

//Call apis to create or update languages
function callCreateOrUpdateApi(
  manipulationType: string,
  formData: IFormData,
  t: TFunction,
  dispatch: AppDispatch
) {
  delete formData['id'];
  if (manipulationType === EActionType.Edit) {
    dispatch(
      updateLanguage({
        request: {
          ...formData,
          name: formData.name.trimEnd(),
          description: formData.description.trim(),
        },
      })
    ).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.update'),
        t('messages.success.update'),
        dispatch
      );
    });
  } else {
    dispatch(
      createLanguage({
        request: {
          ...formData,
          name: formData.name.trimEnd(),
          description: formData.description.trim(),
        },
      })
    ).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.create'),
        t('messages.success.create'),
        dispatch
      );
    });
  }
}

function handleApiResponse(
  res: IAPIResponse,
  failMessage: string,
  successMessage: string,
  dispatch: AppDispatch
) {
  const languages = AppStore.getState().dataMaintainance.languages;
  if (res?.data) {
    dispatch(
      getLanguages({
        page: Number(languages.currentPage),
        limit: Number(languages.recordsPerPage),
        searchCode: languages.searchKey,
      })
    );
    dispatch(setAlertMessage({ show: true, message: successMessage, type: 'success' }));
    dispatch(resetDialogMessage());
  } else {
    const message = res?.error.message ? res.error.message : failMessage;
    dispatch(setAlertMessage({ show: true, message: message, type: 'error' }));
  }
}

const loadFormData = async (
  props: IManipulationProps,
  setInitialLanguageData: React.Dispatch<React.SetStateAction<IFormData>>,
  dispatch: AppDispatch
) => {
  let languageDetails = await dispatch(getLanguageDetails({ request: [props.id] })).unwrap();

  if (!('data' in languageDetails) || !languageDetails.data) {
    return;
  }

  const languageDetails1 = languageDetails.data[0];
  setInitialLanguageData(languageDetails1);
};

const setLocalAlertMessage = (error: IError, setDisplayAlertMessage: (show: boolean) => void) => {
  if (error.code && (error.action === EAction.Create || error.action === EAction.Update)) {
    setDisplayAlertMessage(true);
  }
};

export const LanguageManipulationForm = (props: IManipulationProps) => {
  const { t } = useTranslation();
  const manipulationType = props.type; //To get the type of manipulation being performed
  const [initialLanguageData, setInitialLanguageData] = useState({
    code: '',
    name: '',
    description: '',
    isActive: false,
  });
  const [displayAlertMessage, setDisplayAlertMessage] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const error = useSelector((state: AppState) => state.error);

  useEffect(() => {
    if (manipulationType === EActionType.Edit) {
      loadFormData(props, setInitialLanguageData, dispatch);
    }
  }, []);

  useEffect(() => {
    setLocalAlertMessage(error, setDisplayAlertMessage);
  }, [error]);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch(resetAlertMessage());
    dispatch(resetError());
    setDisplayAlertMessage(false);
  };

  const handleSubmit = (formData: IFormData) => {
    dispatch(resetError());
    setDisplayAlertMessage(false);
    callCreateOrUpdateApi(manipulationType, formData, t, dispatch);
  };

  return (
    <Box className="manipulationBox">
      <Box className="manipulationForm">
        <Collapse in={displayAlertMessage} className="show-alert">
          <Alert className="errorMessage" severity="error" onClose={handleClose}>
            <AlertTitle>
              <ErrorMessage error={error} manipulationType={manipulationType} />
            </AlertTitle>
          </Alert>
        </Collapse>
        <GenericSimpleForm
          formFields={LanguageFormFields}
          initialData={initialLanguageData}
          onSubmit={handleSubmit}
          manipulationType={manipulationType}
          setDisplayAlertMessage={setDisplayAlertMessage}
          type={EDataTableType.Languages}
          uniqueKey="code"
        />
      </Box>
    </Box>
  );
};
